import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';

import { ClientDetailView } from './ClientDetailView.js';
import { useClientQuery } from './useClientsQuery.js';

/**
 * The `/clients/:id` route component. Reads the `id` path param, fires the detail
 * query, and renders `ClientDetailView` on success. Loading → accessible
 * `Spinner`; error → announced error state with a retry (the 3-state contract).
 */

const routeApi = getRouteApi('/clients/$id');

export function ClientDetailRoute() {
  const { t } = useTranslation();
  const { id } = routeApi.useParams();
  const clientId = Number(id);
  const query = useClientQuery(clientId);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError || !query.data.customer) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-8 text-center sm:p-16"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('clients:error.detailHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('clients:error.detailBody')}</p>
        {/* The kit's own secondary Button, not a hand-rolled copy of it. The copy
            that used to sit here had drifted: weight 500 against the kit's 600, no
            press feedback, no disabled handling. A retry on an error screen is the
            one control a reader will reach for, so it wears the same clothes as
            every other secondary action in the app. */}
        <Button type="button" variant="secondary" onClick={() => void query.refetch()}>
          {t('clients:error.retry')}
        </Button>
      </section>
    );
  }

  // Each list is defaulted on its OWN, not just the whole object: a payload that
  // carries `estimates` but omits `jobs` passes an `?? {}` check and then throws on
  // `.jobs.length`, which is a white screen on a client page rather than an empty
  // section. Three sections, three floors.
  const documents = query.data.documents;
  return (
    <ClientDetailView
      customer={query.data.customer}
      documents={{
        estimates: documents?.estimates ?? [],
        invoices: documents?.invoices ?? [],
        jobs: documents?.jobs ?? [],
      }}
    />
  );
}
